import type {
  Accordion,
  ActionBanner,
  BonusGrid,
  Box,
  CTAGrid,
  Carousel,
  CategoryHighlight,
  ContentArea,
  ContentCard,
  DetailsGrid,
  EventSchedule,
  EventTournament,
  FAQ,
  Form,
  GlobalFieldReference,
  Glossary,
  Grid,
  HeaderLink,
  Highlight,
  IconsGrid,
  LeaderBoard,
  LinkedImageList,
  Main,
  MediaGrid,
  Menu,
  Page,
  Panel,
  PaymentVendors,
  Podcast,
  PromotionBanner,
  ReferAFriend,
  Reference,
  SEO,
  SEOFooter,
  Side,
  SideCTA,
  SideRelatedContent,
  SideTwitter,
  SiteFooter,
  SiteHeader,
  StickyFooter,
  Tabs,
  Terms,
  TilesGrid,
  Top,
  Twitch,
  Twitter,
  VideoPanel,
  XMLFeed
} from '@ps/web-martech-cms-types';
import type { SportsReference } from '@ps/web-sports-page-types';
import React from 'react';

import * as CMSContent from '@swf/cms';
import ErrorBoundary from '@swf/components/ErrorBoundary/ErrorBoundary';
import StarsWidget from '@swf/components/StarsWidget/StarsWidget';
import type { WidgetReference } from '@swf/core/types/Widget';
import widgets from '@swf/widgets/index';

const CMSReference: React.FC<Reference | GlobalFieldReference | SportsReference> = (reference) => {
  const contentItem = CMSContent.useCMSReference(reference as Reference);
  const keys = Object.keys(reference);
  let component = null;

  if (keys.length === 1) {
    const [type] = keys;
    // @ts-expect-error - Type issue was not resolved prior to TS5 upgrade.
    const data = reference[type];

    switch (type) {
      case 'html':
        component = <CMSContent.CMSHTML {...data} />;
        break;
      case 'brightcove_video':
        component = <CMSContent.CMSBrightcoveVideo {...data} />;
        break;
      case 'cta':
        component = <CMSContent.CMSCTA {...data} />;
        break;
      case 'client_stats':
        component = <CMSContent.CMSClientState {...data} />;
        break;
      case 'deep_link':
        component = <CMSContent.CMSDeepLink {...data} />;
        break;
      case 'device_based_content':
        component = <CMSContent.CMSDeviceBasedContent {...data} />;
        break;
      case 'heading':
        component = <CMSContent.CMSHeading {...data} />;
        break;
      case 'image':
        component = <CMSContent.CMSImage {...data} />;
        break;
      case 'link':
        component = <CMSContent.CMSLink {...data} />;
        break;
      case 'linked_image':
        component = <CMSContent.CMSLinkedImage {...data} />;
        break;
      case 'notice':
        component = <CMSContent.CMSNotice {...data} />;
        break;
      case 'picture':
        component = <CMSContent.CMSPicture {...data} />;
        break;
      case 'twitter':
        component = <CMSContent.CMSTwitter {...(data as Twitter)} />;
        break;
      case 'twitch':
        component = <CMSContent.CMSTwitch {...(data as Twitch)} />;
        break;
      case 'xml_feed':
        component = <CMSContent.CMSXMLFeed {...(data as XMLFeed)} />;
        break;
    }

    return <ErrorBoundary>{component}</ErrorBoundary>;
  }

  if (!contentItem) return null;

  const { _content_type_uid } = reference as Reference;

  if (Object.keys(widgets).includes(_content_type_uid)) {
    component = <StarsWidget {...(contentItem as WidgetReference)} />;
  } else {
    switch (_content_type_uid) {
      case 'accordion':
        component = <CMSContent.CMSAccordion {...(contentItem as Accordion)} />;
        break;
      case 'bonus_grid':
        component = <CMSContent.CMSBonusGrid {...(contentItem as BonusGrid)} />;
        break;
      case 'box':
        component = <CMSContent.CMSBox {...(contentItem as Box)} />;
        break;
      case 'carousel':
        component = <CMSContent.CMSCarousel {...(contentItem as Carousel)} />;
        break;
      case 'category_highlight':
        component = <CMSContent.CMSCategoryHighlight {...(contentItem as CategoryHighlight)} />;
        break;
      case 'content_area':
        component = <CMSContent.CMSContentArea {...(contentItem as ContentArea)} />;
        break;
      case 'content_card':
        component = <CMSContent.CMSContentCard {...(contentItem as ContentCard)} />;
        break;
      case 'cta_grid':
        component = <CMSContent.CMSCTAGrid {...(contentItem as CTAGrid)} />;
        break;
      case 'details_grid':
        component = <CMSContent.CMSDetailsGrid {...(contentItem as DetailsGrid)} />;
        break;
      case 'event_schedule':
        component = <CMSContent.CMSEventSchedule {...(contentItem as EventSchedule)} />;
        break;
      case 'event_tournament':
        component = <CMSContent.CMSEventTournament {...(contentItem as EventTournament)} />;
        break;
      case 'faq':
        component = <CMSContent.CMSFAQ {...(contentItem as FAQ)} />;
        break;
      case 'form':
        component = <CMSContent.CMSForm {...(contentItem as Form)} />;
        break;
      case 'grid':
        component = <CMSContent.CMSGrid {...(contentItem as Grid)} />;
        break;
      case 'header_link':
        component = <CMSContent.CMSHeaderLink {...(contentItem as HeaderLink)} />;
        break;
      case 'icons_grid':
        component = <CMSContent.CMSIconsGrid {...(contentItem as IconsGrid)} />;
        break;
      case 'leader_board':
        component = <CMSContent.CMSLeaderBoard {...(contentItem as LeaderBoard)} />;
        break;
      case 'linked_image_list':
        component = <CMSContent.CMSLinkedImageList {...(contentItem as LinkedImageList)} />;
        break;
      case 'main':
        component = <CMSContent.CMSMain {...(contentItem as Main)} />;
        break;
      case 'glossary':
        component = <CMSContent.CMSGlossary {...(contentItem as Glossary)} />;
        break;
      case 'highlight':
        component = <CMSContent.CMSHighlight {...(contentItem as Highlight)} />;
        break;
      case 'menu':
        component = <CMSContent.CMSContextMenu {...{ menu: contentItem as Menu }} />;
        break;
      case 'media_grid':
        component = <CMSContent.CMSMediaGrid {...(contentItem as MediaGrid)} />;
        break;
      case 'page':
        component = <CMSContent.CMSPage {...(contentItem as Page)} />;
        break;
      case 'panel':
        component = <CMSContent.CMSPanel {...(contentItem as Panel)} />;
        break;
      case 'payment_vendors':
        component = <CMSContent.CMSPaymentVendors {...(contentItem as PaymentVendors)} />;
        break;
      case 'podcast':
        component = <CMSContent.CMSPodcast {...(contentItem as Podcast)} />;
        break;
      case 'promotion_banner':
        component = <CMSContent.CMSPromotionBanner {...(contentItem as PromotionBanner)} />;
        break;
      case 'refer_a_friend':
        component = <CMSContent.CMSReferAFriend {...(contentItem as ReferAFriend)} />;
        break;
      case 'seo':
        component = <CMSContent.CMSSEO {...(contentItem as SEO)} />;
        break;
      case 'seo_footer':
        component = <CMSContent.CMSSEOFooter {...(contentItem as SEOFooter)} />;
        break;
      case 'side':
        component = <CMSContent.CMSSide {...(contentItem as Side)} />;
        break;
      case 'side_cta':
        component = <CMSContent.CMSSideCTA {...(contentItem as SideCTA)} />;
        break;
      case 'side_related_content':
        component = <CMSContent.CMSSideRelatedContent {...(contentItem as SideRelatedContent)} />;
        break;
      case 'side_twitter':
        component = <CMSContent.CMSSideTwitter {...(contentItem as SideTwitter)} />;
        break;
      case 'site_header':
        component = <CMSContent.CMSHeader {...(contentItem as SiteHeader)} />;
        break;
      case 'site_footer':
        component = <CMSContent.CMSFooter {...(contentItem as SiteFooter)} />;
        break;
      case 'sticky_footer':
        component = <CMSContent.CMSStickyFooter {...(contentItem as StickyFooter)} />;
        break;
      case 'terms':
        component = <CMSContent.CMSTerms {...(contentItem as Terms)} />;
        break;
      case 'top':
        component = <CMSContent.CMSTop {...(contentItem as Top)} />;
        break;
      case 'tiles_grid':
        component = <CMSContent.CMSTilesGrid {...(contentItem as TilesGrid)} />;
        break;
      case 'tabs':
        component = <CMSContent.CMSTabs {...(contentItem as Tabs)} />;
        break;
      case 'video_panel':
        component = <CMSContent.CMSVideoPanel {...(contentItem as VideoPanel)} />;
        break;
      case 'action_banner':
        component = <CMSContent.CMSActionBanner {...(contentItem as ActionBanner)} />;
        break;
    }
  }

  return (
    <ErrorBoundary
      beforeCapture={(scope) => {
        scope.setTag('widget.id', _content_type_uid);
      }}
    >
      {component}
    </ErrorBoundary>
  );
};

export default CMSReference;
